import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { AlertTriangle, Database, Shield, Clock, RefreshCw } from 'lucide-react'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'
import { trpc } from '@/lib/trpc'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'
import { PageHeaderExtra } from '@/components/layout/PageHeaderActions'
import { FilterPill } from '@/components/ui/filter-pill'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/evidence'
)({
  component: EvidenceVaultPage,
  staticData: { pageHeader: { title: 'Evidence Vault' } },
})

type FreshnessFilter = 'all' | 'fresh' | 'stale' | 'expired'
type SourceTypeFilter = 'all' | string

type EvidenceItem = {
  id: string
  statusId: string | null
  sourceType: string
  title: string
  description: string | null
  freshnessState: string
  collectedAt: Date | string
  expiresAt: Date | string
  confidence: string
  collectedBy: string
}

const EMPTY_EVIDENCE_GROUPS: Record<string, EvidenceItem[]> = {}

function FreshnessBadge({ state }: { state: string }) {
  if (state === 'fresh') {
    return (
      <Badge status='green' type='dot' className='font-body h-5 text-[11px]'>
        Fresh
      </Badge>
    )
  }
  if (state === 'stale') {
    return (
      <Badge status='amber' type='dot' className='font-body h-5 text-[11px]'>
        Stale
      </Badge>
    )
  }
  return (
    <Badge status='red' type='dot' className='font-body h-5 text-[11px]'>
      Expired
    </Badge>
  )
}

function SourceTypeBadge({ sourceType }: { sourceType: string }) {
  const label = sourceType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
  return (
    <span
      className='font-body border-status-blue/30 bg-status-blue-bg inline-flex min-h-[20px] max-w-full items-center rounded-sm border px-2 py-0.5 text-[11px] leading-[1.2] font-medium text-status-blue-strong'
      title={label}
    >
      {label}
    </span>
  )
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  if (confidence === 'high') {
    return (
      <Badge status='green' className='font-body h-5 text-[11px]'>
        High confidence
      </Badge>
    )
  }
  if (confidence === 'medium') {
    return (
      <Badge status='amber' className='font-body h-5 text-[11px]'>
        Medium confidence
      </Badge>
    )
  }
  return (
    <Badge status='default' className='font-body h-5 text-[11px]'>
      Low confidence
    </Badge>
  )
}

const SCAN_SOURCE_TYPES = new Set(['cloud_scan', 'repo_scan', 'code_scan'])

function VerificationLabel({ collectedBy }: { collectedBy: string }) {
  const isApiVerified = SCAN_SOURCE_TYPES.has(collectedBy)
  if (isApiVerified) {
    return (
      <span className='font-body text-status-green text-[11px] font-medium'>
        API-verified
      </span>
    )
  }
  return (
    <span className='font-body text-muted-foreground text-[11px]'>
      Manual upload
    </span>
  )
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message
  return 'Something went wrong while loading evidence.'
}

const SOURCE_TYPE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'cloud_scan', label: 'Cloud Scan' },
  { value: 'repo_scan', label: 'Repo Scan' },
  { value: 'code_scan', label: 'Code Scan' },
  { value: 'pr_review', label: 'PR Review' },
  { value: 'policy_attestation', label: 'Policy Attestation' },
  { value: 'manual_upload', label: 'Manual Upload' },
]

function EvidenceVaultPage() {
  const [programId, setProgramId] = useState<string | null>(null)
  const [freshnessFilter, setFreshnessFilter] = useState<FreshnessFilter>('all')
  const [sourceTypeFilter, setSourceTypeFilter] =
    useState<SourceTypeFilter>('all')

  // Get program ID first
  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions()
  )

  useEffect(() => {
    if (programQuery.data?.id) {
      setProgramId(programQuery.data.id)
    }
  }, [programQuery.data])

  // Get evidence summary
  const summaryQuery = useQuery({
    ...trpc.complianceProgram.getEvidenceSummary.queryOptions({
      programId: programId!,
    }),
    enabled: !!programId,
  })

  // Get evidence items with filters applied — returns Record<controlRef, EvidenceItem[]>
  const evidenceQuery = useQuery({
    ...trpc.complianceProgram.getEvidence.queryOptions({
      programId: programId!,
      ...(freshnessFilter !== 'all' ? { freshnessState: freshnessFilter } : {}),
      ...(sourceTypeFilter !== 'all' ? { sourceType: sourceTypeFilter } : {}),
    }),
    enabled: !!programId,
  })

  const summary = summaryQuery.data
  // evidenceQuery.data is Record<controlRef, EvidenceItem[]>
  const grouped = (evidenceQuery.data ?? EMPTY_EVIDENCE_GROUPS) as Record<
    string,
    EvidenceItem[]
  >
  const groupKeys = useMemo(() => Object.keys(grouped).sort(), [grouped])
  const totalItems = useMemo(
    () => new Set(groupKeys.flatMap(key => grouped[key].map(item => item.id))).size,
    [groupKeys, grouped]
  )
  const hasProgramError = programQuery.isError
  const hasDataError = summaryQuery.isError || evidenceQuery.isError

  // ── Page context for workspace chat ──
  const setPageContext = useSetAtom(screenataPageContextAtom)

  useEffect(() => {
    setPageContext({
      page: 'evidence',
      summary: [
        `Evidence Vault: ${summary?.total ?? 0} total evidence items.`,
        `Freshness: ${summary?.byFreshness.fresh ?? 0} fresh, ${summary?.byFreshness.stale ?? 0} stale, ${summary?.byFreshness.expired ?? 0} expired.`,
        summary && summary.total > 0
          ? `${Math.round(summary.freshPercent)}% of evidence is fresh.`
          : '',
        `Showing ${totalItems} items across ${groupKeys.length} controls.`,
      ]
        .filter(Boolean)
        .join(' '),
      data: {
        total: summary?.total ?? 0,
        fresh: summary?.byFreshness.fresh ?? 0,
        stale: summary?.byFreshness.stale ?? 0,
        expired: summary?.byFreshness.expired ?? 0,
        freshPercent: summary ? Math.round(summary.freshPercent) : 0,
        controlCount: groupKeys.length,
        filteredItemCount: totalItems,
      },
    })
    return () => setPageContext(null)
  }, [summary, totalItems, groupKeys, setPageContext])

  if (hasProgramError && !programId) {
    return (
      <div className='flex h-full items-center justify-center px-6'>
        <div className='w-full max-w-md rounded-sm border border-[#E7C7C3] bg-status-red-xsubtle p-5 text-center'>
          <AlertTriangle className='text-status-red mx-auto mb-3 h-5 w-5' />
          <p className='font-body text-sm font-medium text-status-red-ink'>
            Unable to load the compliance program.
          </p>
          <p className='font-body mt-1 text-sm text-[#9E3B2E]'>
            {getErrorMessage(programQuery.error)}
          </p>
          <Button
            type='button'
            variant='outline'
            className='mt-4 h-9 rounded-sm border-[#D9B4AE] bg-card px-3 text-status-red-ink hover:bg-[#FFFDFC]'
            onClick={() => programQuery.refetch()}
          >
            <RefreshCw className='h-4 w-4' />
            Try again
          </Button>
        </div>
      </div>
    )
  }

  if (!programId && !programQuery.isLoading) {
    return (
      <div className='text-muted-foreground flex h-full items-center justify-center px-6 text-center'>
        No compliance program found yet. Start from the chat page to create one.
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-4'>
      {!summaryQuery.isLoading && (
        <PageHeaderExtra>
          <span className='text-muted-foreground text-sm'>
            {totalItems} item{totalItems !== 1 ? 's' : ''} across{' '}
            {groupKeys.length} controls
          </span>
        </PageHeaderExtra>
      )}
      <ScreenataPageHeader
        title='Evidence Vault'
        description='Review collected evidence, filter by freshness, and spot stale items before they block readiness.'
      />
      {/* Summary Stats */}
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
          <div className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
            Total Evidence
          </div>
          <div className='font-heading mt-2 text-[22px] font-bold text-text-strong'>
            {summary?.total ?? 0}
          </div>
        </div>
        <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
          <div className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
            Fresh
          </div>
          <div className='font-heading text-status-green mt-2 text-[22px] font-bold'>
            {summary?.byFreshness.fresh ?? 0}
          </div>
        </div>
        <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
          <div className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
            Stale
          </div>
          <div className='font-heading text-status-orange mt-2 text-[22px] font-bold'>
            {summary?.byFreshness.stale ?? 0}
          </div>
        </div>
        <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
          <div className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
            Expired
          </div>
          <div className='font-heading text-status-red mt-2 text-[22px] font-bold'>
            {summary?.byFreshness.expired ?? 0}
          </div>
        </div>
      </div>

      {/* Freshness progress bar */}
      {summary && summary.total > 0 && (
        <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
          <div className='mb-2 flex items-center justify-between'>
            <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
              Evidence freshness
            </span>
            <span className='font-body text-status-green text-[11px] font-medium'>
              {Math.round(summary.freshPercent)}% fresh
            </span>
          </div>
          <div className='flex h-2 w-full overflow-hidden rounded-sm bg-primary-subtle'>
            <div
              className='bg-status-green h-2 transition-all'
              style={{
                width: `${(summary.byFreshness.fresh / summary.total) * 100}%`,
              }}
            />
            <div
              className='h-2 bg-[#D89A1D] transition-all'
              style={{
                width: `${(summary.byFreshness.stale / summary.total) * 100}%`,
              }}
            />
            <div
              className='bg-status-red h-2 transition-all'
              style={{
                width: `${(summary.byFreshness.expired / summary.total) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Filter Row */}
      <div className='flex flex-wrap items-center gap-3'>
        <div className='flex items-center gap-1'>
          <Shield className='text-text-subtle h-4 w-4' />
          <span className='font-body text-muted-foreground text-[12px]'>
            Freshness:
          </span>
        </div>
        {(['all', 'fresh', 'stale', 'expired'] as FreshnessFilter[]).map(
          (f) => (
            <FilterPill
              key={f}
              active={freshnessFilter === f}
              onClick={() => setFreshnessFilter(f)}
              lightActiveClassName={
                f === 'all'
                  ? 'bg-gray-800 text-white'
                  : f === 'fresh'
                    ? 'bg-emerald-600 text-white'
                    : f === 'stale'
                      ? 'bg-status-orange text-white'
                      : 'bg-red-600 text-white'
              }
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </FilterPill>
          )
        )}

        <div className='mx-1 h-5 w-px bg-border-soft' />

        <div className='flex items-center gap-1'>
          <Clock className='text-text-subtle h-4 w-4' />
          <span className='font-body text-muted-foreground text-[12px]'>
            Source:
          </span>
        </div>
        <Select value={sourceTypeFilter} onValueChange={setSourceTypeFilter}>
          <SelectTrigger className='font-body border-border-divider text-muted-foreground h-8 rounded-sm border bg-card px-3 text-[12px] font-medium'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hasDataError && (
        <div className='rounded-sm border border-[#E7C7C3] bg-status-red-xsubtle p-4'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div className='min-w-0'>
              <p className='font-body text-sm font-medium text-status-red-ink'>
                Unable to refresh the evidence view.
              </p>
              <p className='font-body mt-1 text-sm break-words text-[#9E3B2E]'>
                {getErrorMessage(summaryQuery.error ?? evidenceQuery.error)}
              </p>
            </div>
            <Button
              type='button'
              variant='outline'
              className='h-9 rounded-sm border-[#D9B4AE] bg-card px-3 text-status-red-ink hover:bg-[#FFFDFC]'
              onClick={() => {
                void summaryQuery.refetch()
                void evidenceQuery.refetch()
              }}
            >
              <RefreshCw className='h-4 w-4' />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Evidence List */}
      {evidenceQuery.isLoading || (programId && summaryQuery.isLoading) ? (
        <div
          className='font-body text-muted-foreground py-12 text-center text-[12px]'
          aria-live='polite'
        >
          Loading evidence...
        </div>
      ) : !hasDataError && totalItems === 0 ? (
        <div className='border-border-divider rounded-sm border border-dashed bg-card py-16 text-center'>
          <Database className='text-text-disabled mx-auto mb-3 h-10 w-10' />
          <p className='font-body text-muted-foreground text-[12px]'>
            {freshnessFilter !== 'all' || sourceTypeFilter !== 'all'
              ? 'No evidence items match the selected filters.'
              : 'No evidence collected yet. Run a scan to populate the vault.'}
          </p>
        </div>
      ) : (
        <div className='space-y-6'>
          {groupKeys.map((controlRef) => (
            <div key={controlRef}>
              <div className='mb-3 flex items-center gap-2'>
                <span className='font-body border-status-blue/30 bg-status-blue-bg inline-flex h-5 items-center rounded-sm border px-2 text-[11px] font-semibold text-status-blue-strong'>
                  {controlRef}
                </span>
                <span className='font-body text-muted-foreground text-[11px]'>
                  {grouped[controlRef].length} item
                  {grouped[controlRef].length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className='space-y-2'>
                {grouped[controlRef].map((item) => (
                  <div
                    key={item.id}
                    className='border-border rounded-sm border bg-card p-4 transition-colors hover:border-cobalt-border'
                  >
                    <div className='flex items-start gap-3'>
                      <div className='min-w-0 flex-1'>
                        <div className='flex flex-wrap items-center gap-2'>
                          <h3
                            className='font-heading text-foreground min-w-0 text-[13px] font-semibold break-words'
                            title={item.title}
                          >
                            {item.title}
                          </h3>
                          <FreshnessBadge state={item.freshnessState} />
                          <SourceTypeBadge sourceType={item.sourceType} />
                          <ConfidenceBadge confidence={item.confidence} />
                        </div>
                        {item.description && (
                          <p
                            className='font-body text-muted-foreground mt-1 [display:-webkit-box] overflow-hidden text-[11px] break-words [-webkit-box-orient:vertical] [-webkit-line-clamp:2]'
                            title={item.description}
                          >
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className='font-body border-border text-muted-foreground mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-2.5 text-[11px]'>
                      <div className='flex min-w-0 flex-wrap items-center gap-3'>
                        <span>Collected {formatDate(item.collectedAt)}</span>
                        <span>Expires {formatDate(item.expiresAt)}</span>
                      </div>
                      <div className='flex min-w-0 flex-wrap items-center gap-3'>
                        <VerificationLabel collectedBy={item.collectedBy} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
